/**
 * The legal pages: an index of the documents, and one document.
 *
 * ── WHAT THIS SCREEN IS RESPONSIBLE FOR GETTING RIGHT ───────────────────────
 * It must not overstate what it is showing. A legal page has two possible
 * states and they are not interchangeable:
 *
 *   PUBLISHED — a frozen `LegalDocumentVersion`. It carries a version number,
 *   a date it came into force, and a sha256 of its exact text. That is the
 *   document in force, it is the text a consent record points at, and the page
 *   shows all three so a reader can check the hash against their own export.
 *
 *   DRAFT — the live CMS row, shown only because nothing has been published
 *   for this slug. It is editable, it has no version and no hash, and NOBODY
 *   HAS CONSENTED TO IT. The page therefore shows a "last updated" date and no
 *   version line at all, because printing "Version 1" over an unpublished
 *   draft would be inventing a fact.
 *
 * The distinction is `LegalDocumentView.source`, decided in `cms-api.ts`.
 */
import { formatDate, t } from '@/engine/i18n';
import { getSiteInfo } from '@/core/preview/preview';
import {
  fetchLegalDocument,
  fetchLegalIndex,
  type LegalDocumentView,
  type LegalIndexEntry,
} from './cms-api';
import {
  BUTTON,
  pressable,
  TAP_LINK,
  createPageFrame,
  el,
  mountAsyncPage,
  renderEmpty,
} from './page-parts';
import { applyProseClasses, indexHeadings, renderRichText } from './rich-text';

/** Route for one document. Kept here so the router and the pages agree. */
export const legalHref = (slug: string): string => `/legal/${slug}`;

/** Route for the index. */
export const LEGAL_INDEX_HREF = '/legal';

/* ══════════════════════════════════════════════════════════════════ index ══ */

export function renderLegalIndex(root: HTMLElement): () => void {
  const doc = root.ownerDocument;
  const { main, body } = createPageFrame(doc, {
    title: t('legal.index_title'),
    subtitle: t('legal.index_subtitle'),
  });
  root.replaceChildren(main);

  return mountAsyncPage<LegalIndexEntry[]>(body, {
    load: (signal) => fetchLegalIndex({ signal }),
    notFound: { title: t('legal.not_found_title'), body: t('legal.not_found_body') },
    render: (entries, host) => {
      if (entries.length === 0) {
        renderEmpty(host, t('common.nothing_here'));
        return;
      }
      const list = el(doc, 'ul', 'divide-y divide-border border-y border-border');
      for (const entry of entries) {
        const item = el(doc, 'li', 'py-1');
        const link = el(doc, 'a', `${TAP_LINK} w-full justify-between gap-4`);
        link.href = legalHref(entry.slug);
        link.appendChild(el(doc, 'span', 'text-text', entry.title));
        if (entry.version !== null) {
          link.appendChild(
            el(doc, 'span', 'text-sm text-text-dim', t('legal.version', { version: entry.version })),
          );
        }
        item.appendChild(link);
        list.appendChild(item);
      }
      host.appendChild(list);
    },
  });
}

/* ══════════════════════════════════════════════════════════════ one document ══ */

export function renderLegalPage(root: HTMLElement, slug: string): () => void {
  const doc = root.ownerDocument;
  // The document's own title is the <h1>, so the frame is built with a
  // placeholder and retitled once the text arrives. `legal.title` is a
  // truthful stand-in in the meantime — it never claims to be a document we
  // have not loaded.
  const { main, body } = createPageFrame(doc, {
    title: t('legal.title'),
    backHref: LEGAL_INDEX_HREF,
    backLabel: t('legal.back_to_index'),
  });
  root.replaceChildren(main);
  const h1 = main.querySelector('h1') as HTMLHeadingElement;

  return mountAsyncPage<LegalDocumentView>(body, {
    load: (signal) => fetchLegalDocument(slug, { signal }),
    notFound: { title: t('legal.not_found_title'), body: t('legal.not_found_body') },
    render: (document_, host) => {
      h1.textContent = document_.title;
      host.appendChild(renderProvenance(doc, document_));

      const article = el(doc, 'article', 'mt-6 prose-legal');
      renderRichText(article, document_.content, {
        externalLinkLabel: t('a11y.external_link'),
        doc,
      });
      applyProseClasses(article);

      // `indexHeadings` still runs: it is what gives every <h2> a stable id, so
      // a deep link like /legal/privacy#data-we-hold keeps working. Only the
      // rendered contents list is gone.
      indexHeadings(article);
      host.appendChild(article);

      host.appendChild(renderFooterActions(doc));
    },
  });
}

/**
 * The provenance block: which version this is, when it came into force, and
 * the hash of its text.
 *
 * The hash is shown in full, in a `<code>`, because it is the only thing on
 * the page a reader can independently verify — they can hash the text
 * themselves, or compare it against the value in their data export, and
 * discover for themselves whether the document changed after they agreed to
 * it. Truncating it for looks would remove the only reason it is there.
 */
function renderProvenance(doc: Document, document_: LegalDocumentView): HTMLElement {
  const box = el(doc, 'p', 'text-sm text-text-dim flex flex-wrap gap-x-6 gap-y-1');
  box.dataset.source = document_.source;

  const add = (text: string, className = ''): void => {
    box.appendChild(el(doc, 'span', className, text));
  };

  if (document_.source === 'published' && document_.version !== null) {
    add(t('legal.version', { version: document_.version }));
    if (document_.effectiveFrom !== null) {
      add(t('legal.effective_from', { date: formatDate(new Date(document_.effectiveFrom)) }));
    }
    if (document_.contentHash !== null) {
      const hash = el(doc, 'code', 'font-mono break-all text-xs', `sha256 ${document_.contentHash}`);
      box.appendChild(hash);
    }
  } else if (document_.updatedAt !== null) {
    // No version line: this text is not published, so it is in force for
    // nobody and has no version to name.
    add(t('legal.last_updated', { date: formatDate(new Date(document_.updatedAt)) }));
  }

  return box;
}

/**
 * Print, and a support address if we have one.
 *
 * `/site-info` blanks the company block in preview builds, so an empty email
 * means "not configured" and the line is left out rather than rendered with a
 * hole in it.
 */
function renderFooterActions(doc: Document): HTMLElement {
  const box = el(doc, 'div', 'mt-10 pt-6 border-t border-border flex flex-wrap gap-4 items-center');

  const print = el(doc, 'button', BUTTON, t('legal.print'));
  print.type = 'button';
  pressable(print, 'commerce-invoice', () => {
    // `print` is absent in jsdom, and a page that throws on click is worse
    // than one that quietly does nothing on a platform without a printer.
    if (typeof globalThis.print === 'function') globalThis.print();
  });
  box.appendChild(print);

  const email = getSiteInfo()?.company.email ?? '';
  if (email !== '') {
    const line = el(doc, 'p', 'text-sm text-text-dim', t('legal.contact_prompt', { email }));
    box.appendChild(line);
  }

  return box;
}
